import { createContext, useContext, useEffect, useState } from "react";

const translations = {
  pl: {
    "meta.title": "The Fridge — Na co masz ochotę? Znajdź przepisy.",
    "meta.description":
        "Nie trać czasu na scrollowanie przypadkowych przepisów. The Fridge pomaga szybko przejść od nastroju lub typu posiłku do kilku trafnych propozycji.",
    "nav.openApp": "Otwórz aplikację →",
    "cta.try": "Wypróbuj aplikację",
    "cta.availability": "Dostępne jako aplikacja webowa",
    "hero.badge": "Nie wiesz, na co masz ochotę? Znajdź inspirację.",
    "hero.title.a": "Na co masz ochotę?",
    "hero.title.em": "Znajdź przepisy.",
    "hero.desc":
        "Nie trać czasu na scrollowanie przypadkowych przepisów. The Fridge pomaga szybko przejść od nastroju lub typu posiłku do kilku trafnych propozycji.",
    "how.eyebrow": "Jak to działa",
    "how.title": "Trzy kroki od ochoty do gotowania.",
    "how.1.title": "Wybierz, na co masz ochotę",
    "how.1.desc": "Zacznij od nastroju, a nie od listy składników.",
    "how.2.title": "Zawęź wybór według typu posiłku lub nastroju",
    "how.2.desc": "Śniadanie, obiad, kolacja czy deser — kilka kliknięć wystarczy.",
    "how.3.title": "Otwórz przepis i zacznij gotować",
    "how.3.desc": "Składniki i kroki w czytelnej formie, bez zbędnego szumu.",
    "why.eyebrow": "Dlaczego warto",
    "why.title": "Mniej decyzji. Szybciej do posiłku.",
    "why.1.title": "Mniej decyzji, szybciej do posiłku",
    "why.1.desc": "Krótka ścieżka od pomysłu do konkretnego dania.",
    "why.2.title": "Koniec z bezsensownym scrollowaniem",
    "why.2.desc": "Kilka trafnych propozycji zamiast nieskończonej listy.",
    "why.3.title": "Trafniejsze przepisy na ten moment",
    "why.3.desc": "Propozycje dopasowane do tego, na co faktycznie masz ochotę.",
    "preview.eyebrow": "Zajrzyj do środka",
    "preview.title": "Od nastroju do gotowego przepisu.",
    "preview.cap.1": "Wybierz nastrój",
    "preview.cap.2": "Określ typ posiłku",
    "preview.cap.3": "Przeglądaj propozycje",
    "preview.cap.4": "Zobacz przepis",
    "preview.cap.5": "Sprawdź składniki",
    "preview.cap.6": "Dodaj brakujące produkty i gotuj",
    "problem.eyebrow": "Problem",
    "problem.title": "Od ochoty do przepisu — bez paraliżu decyzyjnego",
    "problem.desc":
        "Najtrudniejsze zwykle nie jest ugotowanie czegoś, z tego co masz w lodówce, tylko decyzja, na co właściwie masz ochotę. The Fridge pomaga Ci ustalić na co właściwie masz ochotę. Podsuwa kilka sensownych propozycji, żebyś nie musiał przeszukiwać całego internetu.",
    "quote": "Zbudowane z myślą o codziennym gotowaniu - po to, żeby wybór posiłku był prostszy.",
    "final.title.a": "Na co masz dziś ochotę?",
    "final.title.em": "Otwórz The Fridge i znajdź przepis szybciej.",
    "social.follow": "Obserwuj nas",
  },
  en: {
    "meta.title": "The Fridge — What are you craving? Find recipes.",
    "meta.description":
        "Stop scrolling through random recipe ideas. The Fridge helps you go from craving or meal type to a few relevant recipes in seconds.",
    "nav.openApp": "Open app →",
    "cta.try": "Try the app",
    "cta.availability": "Available as a web app",
    "hero.badge": "Not sure what you're craving? Find inspiration.",
    "hero.title.a": "What are you craving?",
    "hero.title.em": "Find recipes.",
    "hero.desc":
        "Stop scrolling through random recipe ideas. The Fridge helps you go from craving or meal type to a few relevant recipes in seconds.",
    "how.eyebrow": "How it works",
    "how.title": "Three steps from craving to cooking.",
    "how.1.title": "Pick what you're craving",
    "how.1.desc": "Start from a mood, not from an ingredient list.",
    "how.2.title": "Narrow it down by meal type or mood",
    "how.2.desc": "Breakfast, lunch, dinner or dessert — a couple of taps.",
    "how.3.title": "Open a recipe and start cooking",
    "how.3.desc": "Clear ingredients and steps, without the noise.",
    "why.eyebrow": "Why it's useful",
    "why.title": "Fewer decisions. Faster meals.",
    "why.1.title": "Fewer decisions, faster meals",
    "why.1.desc": "A short path from an idea to an actual dish.",
    "why.2.title": "Less pointless scrolling",
    "why.2.desc": "A few relevant suggestions instead of an endless list.",
    "why.3.title": "Better recipe picks for the moment",
    "why.3.desc": "Suggestions that match what you actually feel like eating.",
    "preview.eyebrow": "A look inside",
    "preview.title": "From a mood to a finished recipe.",
    "preview.cap.1": "Pick a mood",
    "preview.cap.2": "Choose a meal type",
    "preview.cap.3": "Browse suggestions",
    "preview.cap.4": "Open a recipe",
    "preview.cap.5": "Check ingredients",
    "preview.cap.6": "Add missing items and cook",
    "problem.eyebrow": "The problem",
    "problem.title": "From craving to recipe — without decision overload",
    "problem.desc":
        "The hard part usually isn't cooking with what's in your fridge — it's deciding what you actually feel like eating. The Fridge helps you figure out what you're craving and suggests a few relevant options so you don't have to search the whole internet.",
    "quote": "Built for real everyday cooking - designed to make choosing meals easier.",
    "final.title.a": "What are you craving today?",
    "final.title.em": "Open The Fridge and find a recipe faster.",
    "social.follow": "Follow us on social",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState("pl");

  useEffect(() => {
    const stored = (typeof window !== "undefined" && localStorage.getItem("lang"));
    if (stored === "en" || stored === "pl") {
      setLangState(stored);
    }
  }, []);

  const setLang = (l) => {
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem("lang", l);
  };

  const t = (key) => translations[lang][key] ?? translations.en[key] ?? key;

  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
